import { compact } from 'lodash';
import {
  type BasePoint,
  Editor as SlateEditor,
  Element as SlateElement,
  type Location,
  type Node,
  Node as SlateNode,
  type Path,
  Range as SlateRange,
  type RangeRef,
  Text as SlateText,
} from 'slate';

// @ts-expect-error Missing types
import { slateToHtml, slateToText } from 'src/components/RichSlate';

import { SLATE_TEXT_FORMATS } from './const';
import type { CustomSlateElement, SlateImageElement, SlateLinkElement } from './types';

/**
 * Get Slate top-level nodes
 * @param editor - Slate editor instance
 * @returns - Slate nodes
 */
export const getSlateTopLevelNodes = (editor: SlateEditor) => {
  return SlateEditor.nodes(editor, {
    at: [],
    match: (node) => SlateElement.isElement(node),
    mode: 'highest',
  });
};

/**
 * Get Slate top-level block for the passed location
 * @param editor - Slate editor instance
 * @param at - Slate location
 * @returns - Slate block
 */
export const getSlateParentBlock = (editor: SlateEditor, at: Location) => {
  const entry = SlateEditor.above(editor, {
    at,
    match: (node) =>
      !SlateEditor.isEditor(node) &&
      SlateElement.isElement(node) &&
      SlateEditor.isBlock(editor, node),
    mode: 'highest',
  });
  return entry?.[0];
};

/**
 * Get text of the Slate top-level block for the passed location
 * @param editor - Slate editor instance
 * @param at - Slate location
 * @returns - Block text
 */
export const getSlateBlockText = (editor: SlateEditor, at: Location) => {
  const blockNode = getSlateParentBlock(editor, at);
  return blockNode ? SlateNode.string(blockNode) : '';
};

/**
 * Get Slate range of the whole editor content
 * @param editor - Slate editor instance
 * @returns - Slate range
 */
export const getSlateRangeFull = (editor: SlateEditor) => {
  const startPoint = SlateEditor.start(editor, []);
  const endPoint = SlateEditor.end(editor, []);
  return SlateEditor.range(editor, startPoint, endPoint);
};

/**
 * Get Slate range before the passed location
 * @param editor - Slate editor instance
 * @param location - Slate location
 * @returns - Slate range
 */
const getSlateRangeBefore = (editor: SlateEditor, location: Location) => {
  const startPoint = SlateEditor.start(editor, []);
  return SlateEditor.range(editor, startPoint, location);
};

/**
 * Get Slate range after the passed location
 * @param editor - Slate editor instance
 * @param location - Slate location
 * @returns - Slate range
 */
const getSlateRangeAfter = (editor: SlateEditor, location: Location) => {
  const endPoint = SlateEditor.end(editor, []);
  return SlateEditor.range(editor, location, endPoint);
};

/**
 * Get text value before the passed location
 * @param editor - Slate editor instance
 * @param location - Slate location
 * @returns - Text value
 */
export const getSlateTextBefore = (editor: SlateEditor, location: Location) => {
  if (!editor || !location) {
    return null;
  }

  const beforeRange = getSlateRangeBefore(editor, location);
  return SlateEditor.string(editor, beforeRange);
};

/**
 * Get text value after the passed location
 * @param editor - Slate editor instance
 * @param location - Slate location
 * @returns - Text value
 */
export const getSlateTextAfter = (editor: SlateEditor, location: Location) => {
  if (!editor || !location) {
    return null;
  }

  const afterRange = getSlateRangeAfter(editor, location);
  return SlateEditor.string(editor, afterRange);
};

/**
 * Get Slate fragment before the passed location
 * @param editor - Slate editor instance
 * @param location - Slate location
 * @returns - Slate fragment
 */
export const getSlateFragmentBefore = (editor: SlateEditor, location: Location) => {
  if (!editor || !location) {
    return null;
  }

  const beforeRange = getSlateRangeBefore(editor, location);
  return SlateNode.fragment(editor, beforeRange);
};

/**
 * Get Slate fragment after the passed location
 * @param editor - Slate editor instance
 * @param location - Slate location
 * @returns - Slate fragment
 */
export const getSlateFragmentAfter = (editor: SlateEditor, location: Location) => {
  if (!editor || !location) {
    return null;
  }

  const afterRange = getSlateRangeAfter(editor, location);
  return SlateNode.fragment(editor, afterRange);
};

/**
 * Get text value of the passed range
 * @param editor - Slate editor instance
 * @param range - Slate range
 * @returns - Text value
 */
export const getSlateRangeText = (editor: SlateEditor, range: SlateRange) => {
  if (!range || SlateRange.isCollapsed(range)) {
    return '';
  }

  return SlateEditor.string(editor, range);
};

/**
 * Get text value before the passed range
 * @param editor - Slate editor instance
 * @param range - Slate range
 * @returns - Text value
 */
export const getSlateTextBeforeRange = (editor: SlateEditor, range: SlateRange) => {
  if (!range) {
    return '';
  }

  const rangeEdges = SlateRange.edges(range);
  return getSlateTextBefore(editor, rangeEdges[0]);
};

/**
 * Get text value after the passed range
 * @param editor - Slate editor instance
 * @param range - Slate range
 * @returns - Text value
 */
export const getSlateTextAfterRange = (editor: SlateEditor, range: SlateRange) => {
  if (!range) {
    return '';
  }

  const rangeEdges = SlateRange.edges(range);
  return getSlateTextAfter(editor, rangeEdges[1]);
};

/**
 * Get Slate fragment from the current selection
 * @param editor - Slate editor instance
 * @returns - Slate fragment
 */
export const getSlateSelectionFragment = (editor: SlateEditor) => {
  if (!editor?.selection) {
    return null;
  }

  if (SlateRange.isCollapsed(editor.selection)) {
    return [];
  }

  return SlateNode.fragment(editor, editor.selection);
};

/**
 * Get Slate fragment before the current selection
 * @param editor - Slate editor instance
 * @returns - Slate fragment
 */
export const getSlateFragmentBeforeSelection = (editor: SlateEditor) => {
  if (!editor?.selection) {
    return null;
  }

  const selectionEdges = SlateRange.edges(editor.selection);
  return getSlateFragmentBefore(editor, selectionEdges[0]);
};

/**
 * Get Slate fragment after the current selection
 * @param editor - Slate editor instance
 * @returns - Slate fragment
 */
export const getSlateFragmentAfterSelection = (editor: SlateEditor) => {
  if (!editor?.selection) {
    return null;
  }

  const selectionEdges = SlateRange.edges(editor.selection);
  return getSlateFragmentAfter(editor, selectionEdges[1]);
};

/**
 * Get Slate nodes that are part of the current selection
 * @param editor - Slate editor instance
 * @returns - Slate nodes
 */
export const getSlateNodeEntriesAtSelection = (editor: SlateEditor) => {
  if (!editor?.selection) {
    return null;
  }

  if (SlateRange.isCollapsed(editor.selection)) {
    return [];
  }

  return [
    ...SlateEditor.nodes(editor, {
      at: editor.selection,
      match: (node) =>
        !SlateEditor.isEditor(node) &&
        SlateElement.isElement(node) &&
        SlateEditor.isBlock(editor, node),
    }),
  ];
};

export const characterBefore = (editor: SlateEditor, basePoint: BasePoint) => {
  const beforePoint = SlateEditor.before(editor, basePoint, { unit: 'character' });
  if (!beforePoint) {
    return null;
  }
  return SlateEditor.string(editor, { anchor: beforePoint, focus: basePoint });
};

export const characterAfter = (editor: SlateEditor, basePoint: BasePoint) => {
  const afterPoint = SlateEditor.after(editor, basePoint, { unit: 'character' });
  if (!afterPoint) {
    return null;
  }
  return SlateEditor.string(editor, { anchor: basePoint, focus: afterPoint });
};

/**
 * Create a Slate range given the start and end text indexes in the editor
 * @param editor - Slate editor instance
 * @param startIndex - Start index
 * @param endIndex - End index
 * @returns - Slate range
 */
export const createSlateRange = (editor: SlateEditor, startIndex: number, endIndex: number) => {
  const { children } = editor;

  let start: BasePoint | null = null;
  let end: BasePoint | null = null;

  let currentOffset = 0;
  const findPoint = (node: Node, path: number[] = [], isRoot = false) => {
    if (SlateText.isText(node)) {
      const textLength = node.text.length;

      // Check if startIndex is in the current node
      if (startIndex >= currentOffset && startIndex <= currentOffset + textLength) {
        start = {
          path,
          offset: startIndex - currentOffset,
        };
      }

      // Check if endIndex is in the current node
      if (endIndex >= currentOffset && endIndex <= currentOffset + textLength) {
        end = {
          path,
          offset: endIndex - currentOffset,
        };
      }

      currentOffset += textLength;
    } else {
      // Recursively find in child nodes
      for (let i = 0; i < node.children.length; i++) {
        findPoint(node.children[i], path.concat(i));
        if (start && start.path?.length > 0 && end && end.path?.length > 0) {
          break;
        }

        // Account for newlines between paragraph nodes
        if (isRoot) {
          currentOffset += 1;
        }
      }
    }
  };

  findPoint({ children }, [], true);

  // Create a range from the points
  return {
    anchor: start || SlateEditor.start(editor, []),
    focus: end || SlateEditor.end(editor, []),
  };
};

/**
 * Get the range references intersection with the node
 * @param editor - Slate editor instance
 * @param rangeRefs - Range references
 * @param nodePath - Node path
 * @returns - Intersections
 */
export const getRangeRefsIntersectionsWithNode = (
  editor: SlateEditor,
  rangeRefs: RangeRef[],
  nodePath: Path
) => {
  const nodeRange = SlateEditor.range(editor, nodePath);
  const intersections = rangeRefs.map((rangeRef) => {
    if (!rangeRef.current) {
      return null;
    }
    return SlateRange.intersection(rangeRef.current, nodeRange);
  });
  return compact(intersections);
};

/**
 * Get the plain text content of the Slate editor, including line breaks
 * @param editor - Slate editor instance
 * @returns - Plain text content of the editor with line breaks
 */
export const getEditorTextContent = (editor: SlateEditor) => {
  if (!editor) {
    return '';
  }

  // Utilize the existing slateToText utility
  return slateToText(editor.children);
};

/**
 * Get the HTML content of the Slate editor
 * @param editor - Slate editor instance
 * @returns - HTML content of the editor
 */
export const getEditorHtmlContent = (editor: SlateEditor) => {
  if (!editor) {
    return '';
  }

  // Utilize the existing slateToHtml utility
  return slateToHtml(editor.children);
};

/**
 * Checks if the Slate editor is empty.
 * @param editor - The Slate editor instance.
 * @returns - True if the editor is empty, false otherwise.
 */
export const isEditorEmpty = (editor: SlateEditor) => SlateNode.string(editor).trim() === '';

/**
 * Checks if the current text cursor is in an empty line in the Slate editor.
 * @param editor - The Slate editor instance.
 * @returns - True if the cursor is in an empty line, false otherwise.
 */
export const isCursorInEmptyLine = (editor: SlateEditor) => {
  if (!editor.selection || !SlateRange.isCollapsed(editor.selection)) {
    return false; // No selection or selection is not collapsed (cursor is not alone).
  }

  const [currentNode] = SlateEditor.node(editor, editor.selection);
  return SlateNode.string(currentNode).trim() === '';
};

/**
 * Get the URL of the selected link in the Slate editor.
 * @param editor - The Slate editor instance.
 * @returns - The URL of the selected link, or null if no link is selected.
 */
export const getEditorSelectedLinkNode = (editor: SlateEditor) => {
  if (!editor?.selection) {
    return null;
  }

  const entries = SlateEditor.nodes(editor, {
    at: editor.selection.anchor.path,
    match: (node) => isSlateLinkNode(node as CustomSlateElement),
  });

  const entriesArray = [...entries];
  return entriesArray[0]?.[0];
};

export const isSlateLinkNode = (node: CustomSlateElement): node is SlateLinkElement =>
  node.type === SLATE_TEXT_FORMATS.LINK;

export const isSlateImageNode = (node: CustomSlateElement): node is SlateImageElement =>
  node.type === SLATE_TEXT_FORMATS.IMG;
